import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import clinicAPI from "../services/clinicApi";
import {
  getSubdomainFromHost,
  isClinicPortal,
  isPlatformHost,
} from "../utils/portalHost";
import { setStoredSubdomain } from "../utils/clinicStorage";

const PortalContext = createContext({
  subdomain: null,
  clinic: null,
  isPortal: false,
  isPlatform: true,
  loading: false,
  notFound: false,
});

export function PortalProvider({ children }) {
  const subdomain = getSubdomainFromHost();
  const isPortal = isClinicPortal();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(isPortal);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!isPortal || !subdomain) {
      setLoading(false);
      setNotFound(false);
      setClinic(null);
      return;
    }

    setStoredSubdomain(subdomain);
    setLoading(true);
    setNotFound(false);

    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 10000);

    clinicAPI
      .getPublicClinic(subdomain)
      .then((r) => {
        if (cancelled) return;
        setClinic(r.clinic || null);
        setNotFound(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setClinic(null);
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => {
        cancelled = true;
        clearTimeout(timeoutId);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isPortal, subdomain]);

  const value = useMemo(
    () => ({
      subdomain,
      clinic,
      isPortal,
      isPlatform: isPlatformHost(),
      loading,
      notFound,
    }),
    [subdomain, clinic, isPortal, loading, notFound],
  );

  return (
    <PortalContext.Provider value={value}>{children}</PortalContext.Provider>
  );
}

export function usePortal() {
  return useContext(PortalContext);
}
